import { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Package,
  Tag,
  MessageSquare,
  LogOut,
  Menu,
  X,
  TrendingUp,
  Settings as SettingsIcon,
  Calendar,
  Users,
  MapPin,
  Database,
  Activity
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/auth');
  };

  const navItems = [
    {
      name: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin/dashboard',
    },
    {
      name: 'User Management',
      icon: Users,
      path: '/admin/users',
    },
    {
      name: 'Orders',
      icon: Package,
      path: '/admin/orders',
    },
    {
      name: 'Coupons',
      icon: Tag,
      path: '/admin/coupons',
    },
    {
      name: 'Customer Reviews',
      icon: MessageSquare,
      path: '/admin/reviews',
    },
    {
      name: 'Pricing Settings',
      icon: SettingsIcon,
      path: '/admin/pricing/settings',
    },
    {
      name: 'Surge Pricing',
      icon: Calendar,
      path: '/admin/pricing/surge',
    },
    {
      name: 'Pricing Analytics',
      icon: TrendingUp,
      path: '/admin/pricing/analytics',
    },
    {
      name: 'Delivery Settings',
      icon: MapPin,
      path: '/admin/delivery/settings',
    },
    {
      name: 'Store Scraper',
      icon: Database,
      path: '/admin/stores/scraper',
    },
    {
      name: 'Agent Economics',
      icon: Activity,
      path: '/admin/agent-economics',
    },
  ];

  return (
    <div data-site-shell className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div
        data-site-header
        className="fixed inset-x-0 top-0 z-50 flex h-16 items-center justify-between bg-[#0E1A2F] p-4 text-white lg:hidden"
      >
        <h1 className="text-xl font-bold">Style.re Admin</h1>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-white/10 rounded-md"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div data-site-content className="mt-16 flex lg:mt-0">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 h-screen bg-[#0E1A2F] text-white w-64 z-40
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 border-b border-white/10 hidden lg:block">
              <h1 className="text-2xl font-bold">Style.re Admin</h1>
              <p className="text-sm text-gray-400 mt-1">Dashboard</p>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                      ${isActive 
                        ? 'bg-primary text-white' 
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Logout */}
            <div className="p-4 border-t border-white/10">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10"
              >
                <LogOut size={20} className="mr-3" />
                Logout
              </Button>
              <Link
                to="/"
                className="block mt-2 text-sm text-gray-400 hover:text-white px-4 py-2 rounded transition-colors"
              >
                ← Back to Customer Site
              </Link>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
}

